import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Card, CardBody, CardHeader, Table, Button,
    Input, FormGroup, Label, FormFeedback, Row, Col, Badge,
} from 'reactstrap';

const EMPTY_FORM = { currency_code: '', campaign_id: '', client_id: '' };

const NeoCurrencyCampaignManager = ({ rewardPlatformId }) => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const fetchCampaigns = async () => {
        try {
            const res = await axios.get('/reward-platform-campaign', {
                params: { reward_platform_id: rewardPlatformId },
            });
            setCampaigns(res.data);
        } catch (e) {
            console.error('Failed to load campaigns', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (rewardPlatformId) fetchCampaigns();
    }, [rewardPlatformId]);

    const validate = () => {
        const errs = {};
        if (!form.currency_code || form.currency_code.length !== 3) {
            errs.currency_code = 'Required — 3-letter ISO code (e.g. USD)';
        }
        if (!form.campaign_id || isNaN(parseInt(form.campaign_id))) {
            errs.campaign_id = 'Required — numeric campaign ID from NeoCurrency';
        }
        if (!form.client_id || isNaN(parseInt(form.client_id))) {
            errs.client_id = 'Required — numeric client ID from NeoCurrency';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleAdd = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await axios.post('/reward-platform-campaign', {
                reward_platform_id: rewardPlatformId,
                currency_code: form.currency_code.toUpperCase(),
                campaign_id: parseInt(form.campaign_id),
                client_id: parseInt(form.client_id),
                status: 1,
            });
            setForm(EMPTY_FORM);
            setErrors({});
            await fetchCampaigns();
        } catch (e) {
            const msg = e.response?.data?.error || 'Failed to save';
            setErrors({ submit: msg });
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (campaign) => {
        try {
            await axios.put(`/reward-platform-campaign/${campaign.id}`, {
                status: campaign.status === 1 ? 0 : 1,
            });
            await fetchCampaigns();
        } catch (e) {
            alert('Failed to update status');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this campaign mapping?')) return;
        try {
            await axios.delete(`/reward-platform-campaign/${id}`);
            await fetchCampaigns();
        } catch (e) {
            alert('Failed to delete campaign mapping');
        }
    };

    if (!rewardPlatformId) return null;

    return (
        <Card className="mt-4">
            <CardHeader><strong>NeoCurrency Campaign Map</strong></CardHeader>
            <CardBody>
                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <>
                        <Table size="sm" bordered responsive>
                            <thead>
                                <tr>
                                    <th>Currency</th>
                                    <th>Campaign ID</th>
                                    <th>Client ID</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center text-muted">
                                            No campaigns configured. Add one below.
                                        </td>
                                    </tr>
                                ) : campaigns.map(c => (
                                    <tr key={c.id}>
                                        <td><strong>{c.currency_code}</strong></td>
                                        <td>{c.campaign_id}</td>
                                        <td>{c.client_id}</td>
                                        <td>
                                            <Badge color={c.status === 1 ? 'success' : 'secondary'}>
                                                {c.status === 1 ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button
                                                size="sm"
                                                color={c.status === 1 ? 'warning' : 'success'}
                                                className="mr-1"
                                                onClick={() => handleToggleStatus(c)}
                                            >
                                                {c.status === 1 ? 'Disable' : 'Enable'}
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                onClick={() => handleDelete(c.id)}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <hr />
                        <p className="text-muted small mb-2">
                            Add a currency → campaign mapping. Run <code>php artisan neocurrency:campaigns</code> to discover campaign IDs from the vendor API.
                        </p>
                        <Row form>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>Currency Code</Label>
                                    <Input
                                        type="text"
                                        placeholder="USD"
                                        maxLength={3}
                                        value={form.currency_code}
                                        onChange={e => setForm(f => ({ ...f, currency_code: e.target.value }))}
                                        invalid={!!errors.currency_code}
                                    />
                                    <FormFeedback>{errors.currency_code}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Campaign ID</Label>
                                    <Input
                                        type="number"
                                        placeholder="2804"
                                        value={form.campaign_id}
                                        onChange={e => setForm(f => ({ ...f, campaign_id: e.target.value }))}
                                        invalid={!!errors.campaign_id}
                                    />
                                    <FormFeedback>{errors.campaign_id}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Client ID</Label>
                                    <Input
                                        type="number"
                                        placeholder="683"
                                        value={form.client_id}
                                        onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                                        invalid={!!errors.client_id}
                                    />
                                    <FormFeedback>{errors.client_id}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={2} className="d-flex align-items-end mb-3">
                                <Button color="primary" onClick={handleAdd} disabled={saving}>
                                    {saving ? 'Saving…' : 'Add'}
                                </Button>
                            </Col>
                        </Row>
                        {errors.submit && (
                            <p className="text-danger small">{errors.submit}</p>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default NeoCurrencyCampaignManager;
